import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, Target, ArrowLeft, Star, Rocket, CheckCircle, TrendingUp, Zap, Mountain, Infinity, Crown } from 'lucide-react';
import { Button } from '../components/Button';
import { ProtectedLayout } from '../components/ProtectedLayout';

export function ScoringExplanation() {
  const navigate = useNavigate();

  return (
        <ProtectedLayout>  
    <div className="min-h-screen bg-gradient-to-b from-white to-blue-50">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <Trophy className="w-8 h-8 text-[#236caf]" />
              <h1 className="text-3xl font-bold text-[#004060]">Core Victory Method Scoring</h1>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </Button>
          </div>

          <div className="space-y-12">
            {/* Introduction */}
            <div>
              <h2 className="text-2xl font-semibold text-[#004060] mb-4">The Power of Intentional Action</h2>
              <p className="text-gray-600 mb-4">
                The Core Victory Method isn't about perfection—it's about progress. By turning your growth into a game,
                you'll naturally increase the number of intentional, aligned actions you take each day.
              </p>
              <div className="bg-blue-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-[#004060] mb-3">How You Earn Points</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Target className="w-5 h-5 text-[#236caf]" />
                    <div>
                      <span className="font-semibold text-[#004060]">Daily Journal Entry: 20 points</span>
                      <p className="text-sm text-gray-600">Builds clarity and awareness</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Target className="w-5 h-5 text-[#236caf]" />
                    <div>
                      <span className="font-semibold text-[#004060]">Intentional Action: 10 points each</span>
                      <p className="text-sm text-gray-600">Creates momentum and results</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Daily Badges */}
            <div>
              <h2 className="text-2xl font-semibold text-[#004060] mb-4">Daily Badges</h2>
              <p className="text-gray-600 mb-6">These badges reflect your daily commitment to growth:</p>
              <div className="grid gap-4">
                <div className="p-4 bg-gradient-to-r from-blue-50 to-white rounded-lg flex items-start gap-4">
                  <div className="p-2 bg-white rounded-full">
                    <Star className="w-6 h-6 text-[#236caf]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#004060]">Showing Up (20-30 points)</h3>
                    <p className="text-gray-600">You stayed in the game — and that matters.</p>
                  </div>
                </div>
                <div className="p-4 bg-gradient-to-r from-blue-50 to-white rounded-lg flex items-start gap-4">
                  <div className="p-2 bg-white rounded-full">
                    <Rocket className="w-6 h-6 text-[#236caf]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#004060]">Making Progress (40-60 points)</h3>
                    <p className="text-gray-600">Solid effort — momentum is building.</p>
                  </div>
                </div>
                <div className="p-4 bg-gradient-to-r from-blue-50 to-white rounded-lg flex items-start gap-4">
                  <div className="p-2 bg-white rounded-full">
                    <Trophy className="w-6 h-6 text-[#236caf]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#004060]">High Performer (70+ points)</h3>
                    <p className="text-gray-600">You're taking big, aligned action. Keep it up.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Weekly Badges */}
            <div>
              <h2 className="text-2xl font-semibold text-[#004060] mb-4">Weekly Badges</h2>
              <p className="text-gray-600 mb-6">Track your consistency throughout the week:</p>
              <div className="grid gap-4">
                <div className="p-4 bg-gradient-to-r from-green-50 to-white rounded-lg flex items-start gap-4">
                  <div className="p-2 bg-white rounded-full">
                    <CheckCircle className="w-6 h-6 text-[#318231]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#004060]">Consistent Effort (150-200 points)</h3>
                    <p className="text-gray-600">You showed up most days — progress guaranteed.</p>
                  </div>
                </div>
                <div className="p-4 bg-gradient-to-r from-green-50 to-white rounded-lg flex items-start gap-4">
                  <div className="p-2 bg-white rounded-full">
                    <TrendingUp className="w-6 h-6 text-[#318231]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#004060]">Growth Week (250-300 points)</h3>
                    <p className="text-gray-600">Strong, intentional action — this is where things shift.</p>
                  </div>
                </div>
                <div className="p-4 bg-gradient-to-r from-green-50 to-white rounded-lg flex items-start gap-4">
                  <div className="p-2 bg-white rounded-full">
                    <Zap className="w-6 h-6 text-[#318231]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#004060]">Breakthrough Week (350+ points)</h3>
                    <p className="text-gray-600">Big energy, big effort, big results. Well done.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Monthly Badges */}
            <div>
              <h2 className="text-2xl font-semibold text-[#004060] mb-4">Monthly Badges</h2>
              <p className="text-gray-600 mb-6">Celebrate your long-term momentum:</p>
              <div className="grid gap-4">
                <div className="p-4 bg-gradient-to-r from-purple-50 to-white rounded-lg flex items-start gap-4">
                  <div className="p-2 bg-white rounded-full">
                    <Mountain className="w-6 h-6 text-[#6b46c1]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#004060]">Steady Progress (600-800 points)</h3>
                    <p className="text-gray-600">The foundation is built — keep stacking wins.</p>
                  </div>
                </div>
                <div className="p-4 bg-gradient-to-r from-purple-50 to-white rounded-lg flex items-start gap-4">
                  <div className="p-2 bg-white rounded-full">
                    <Infinity className="w-6 h-6 text-[#6b46c1]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#004060]">Momentum Builder (900-1200 points)</h3>
                    <p className="text-gray-600">You're creating results that compound over time.</p>
                  </div>
                </div>
                <div className="p-4 bg-gradient-to-r from-purple-50 to-white rounded-lg flex items-start gap-4">
                  <div className="p-2 bg-white rounded-full">
                    <Crown className="w-6 h-6 text-[#6b46c1]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#004060]">All-In Month (1200+ points)</h3>
                    <p className="text-gray-600">You gave it everything — and it shows. This is rare air.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Why It Works */}
            <div className="bg-gradient-to-r from-[#004060] to-[#236caf] text-white p-8 rounded-lg">
              <h2 className="text-2xl font-semibold mb-4">Why It Works</h2>
              <p className="mb-6">
                This isn't about doing more. It's about doing what matters — consistently.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-white rounded-full" />
                  <p>Journal to stay clear and focused on what matters most</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-white rounded-full" />
                  <p>Take action to create tangible results in your life</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-white rounded-full" />
                  <p>Repeat to build mastery and lasting change</p>
                </div>
              </div>
              <div className="mt-8 text-lg font-medium">
                Your points tell the story of your effort. Your badges remind you how far you've come.
              </div>
            </div>

            <div className="text-center text-gray-600 italic">
              Keep showing up. Keep moving forward. Your best work is built one action at a time.
            </div>
          </div>
        </div>
      </div>
    </div>
        </ProtectedLayout>
  );
}